/**
 * Generator 7: Page (server) — (authenticated)/<n>/page.tsx
 * Generates the server page component that renders columns + page content
 */

import type { GeneratedFile, GeneratorOptions, Schema } from '@core-engine/schema/types.js'
import { createGeneratedFile } from '@core-engine/utils/generated-files.js'
import { pluralize, toKebabCase, toPascalCase } from '@core-engine/utils/string.js'

// ============================================================================
// Public API
// ============================================================================

export interface PageGeneratorResult {
  files: GeneratedFile[]
}

/**
 * Generate server page component for entity list view
 */
export function generatePage(
  schema: Schema,
  pagesDir: string,
  _options: GeneratorOptions = {}
): PageGeneratorResult {
  const plural = pluralize(schema.name)
  const Plural = toPascalCase(plural)
  const kebabPlural = toKebabCase(plural)

  const content = `import { getSetting } from '@admin/actions/settings'
import * as React from 'react'
import { columns } from './columns'
import { ${Plural}PageContent } from './${kebabPlural}-page-content'
import { ${Plural}PageSkeleton } from './${kebabPlural}-page-skeleton'

export default async function ${Plural}Page() {
  const initialSettings = await getSetting()

  return (
    <React.Suspense fallback={<${Plural}PageSkeleton />}>
      <${Plural}PageContent columns={columns} initialSettings={initialSettings} />
    </React.Suspense>
  )
}
`
  const skeletonContent = `export function ${Plural}PageSkeleton() {
  return (
    <div className="flex items-center justify-center h-48">
      <div className="text-muted-foreground">Loading ${schema.label}…</div>
    </div>
  )
}
`

  return {
    files: [
      createGeneratedFile(`${pagesDir}/${schema.name}/page.tsx`, content),
      createGeneratedFile(
        `${pagesDir}/${schema.name}/${kebabPlural}-page-skeleton.tsx`,
        skeletonContent
      )
    ]
  }
}
